import { Component, Input } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from '../../kuba.services';
import { complianceReportFileName, FlexiComplianceService } from './flexi-compliance.service';
import {
  FlexiCompliance,
  FlexiComplianceActionPointStatus,
  FlexiComplianceHistoryItem,
  FlexiComplianceLineItem,
  FlexiComplianceRating,
  FlexiComplianceResponsibleParty,
  FlexiComplianceSection,
  FlexiComplianceStatus,
  HasResponsibleParty,
  responsiblePartyNameSuffix
} from './flexi-compliance.models';
import { EMOJI_ENTRY_ANIMATE, EMOJI_ENTRY_IDLE, flexiEmojiEntry } from '../animations/emoji-entry.animation';
import { FlexiEmojiEntryService } from '../services/flexi-emoji-entry.service';

type ComplianceSectionTone =
  | 'pending' | 'in-progress' | 'red' | 'yellow' | 'green';

/**
 * Customer read-only view for BHT-samsvar. Only renders when the
 * assessment has been Completed (resolver gates this via "locked" vs
 * "customer" role). Shows the score smiley + per-item rating chip.
 */
@Component({
  selector: 'flexi-compliance-final-view',
  templateUrl: './compliance-final-view.component.html',
  styleUrls: ['./flexi-compliance.scss'],
  animations: [flexiEmojiEntry],
  standalone: false
})
export class ComplianceFinalViewComponent {
  @Input() aggregate!: FlexiCompliance;

  /**
   * Suppresses the "Previous reviews" strip. Set false when this component is
   * embedded as the read-only renderer inside another page that already shows
   * its own strip, so the two don't stack.
   */
  @Input() showHistory = true;

  /**
   * Set when the input aggregate is the business's published result from
   * GET /final. That can be a Superseded review — the previous result, kept
   * while the advisor has a new review in draft — and to the customer it is
   * still the current result, so it is labelled Completed rather than
   * "Superseded", which would send them looking for a replacement they cannot
   * see yet. A review peeked from the history strip keeps its real status.
   */
  @Input() asPublishedResult = false;

  /**
   * A past review being previewed in place, from the history strip. Null means
   * we're showing the input aggregate.
   */
  peeked: FlexiCompliance | null = null;
  peekedOn: string | null = null;
  peekLoading = false;

  /** What the template renders — the preview when there is one. */
  get viewed(): FlexiCompliance {
    return this.peeked ?? this.aggregate;
  }

  /** Status the header pill shows — see {@link asPublishedResult}. */
  get displayStatus(): FlexiComplianceStatus {
    const status = this.viewed.status;
    return this.asPublishedResult && !this.peeked && status === FlexiComplianceStatus.Superseded
      ? FlexiComplianceStatus.Completed
      : status;
  }

  readonly Rating = FlexiComplianceRating;
  readonly ApStatus = FlexiComplianceActionPointStatus;
  readonly Status = FlexiComplianceStatus;

  /**
   * " - Kari Nordmann" for the responsible chip. Appended to the translated
   * role so a chip names the person and not only the side. Empty when the role
   * has no holder — see responsiblePartyNameSuffix.
   */
  responsibleNameSuffix(action: HasResponsibleParty): string {
    return responsiblePartyNameSuffix(action);
  }

  /** Helper for the responsible-party chip label. */
  responsibleLabelKey(party: FlexiComplianceResponsibleParty | null): string {
    return party === FlexiComplianceResponsibleParty.Advisor
      ? 'FLEXI_COMPLIANCE.RESPONSIBLE.ADVISOR'
      : 'FLEXI_COMPLIANCE.RESPONSIBLE.CUSTOMER';
  }

  /** Download-button state — same UX latch + error banner as the CSM rating view. */
  downloading = false;
  errorMessage: string | null = null;

  /**
   * Loads a past review and shows it in place. Null returns to the live one.
   *
   * Read-only either way: this component never mutates, so previewing a
   * snapshot needs no extra guarding — the sections simply render whatever
   * aggregate they are handed.
   */
  onHistorySelected(row: FlexiComplianceHistoryItem | null): void {
    if (!row) {
      this.peeked = null;
      this.peekedOn = null;
      return;
    }
    this.peekLoading = true;
    this.errorMessage = null;
    this.service.getReview(this.aggregate.businessId, row.id).subscribe({
      next: agg => {
        this.peeked = agg;
        this.peekedOn = row.completedOn;
        this.peekLoading = false;
        // Collapse the sections so the preview lands on the score, matching
        // how the page itself opens.
        this.activeSectionId = null;
      },
      error: (err: HttpErrorResponse) => {
        this.peekLoading = false;
        this.errorMessage = err?.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.LOAD_REVIEW_FAILED');
      }
    });
  }

  backToCurrent(): void {
    this.onHistorySelected(null);
  }

  constructor(
    private translate: TranslateService,
    private service: FlexiComplianceService,
    private emojiEntry: FlexiEmojiEntryService
  ) {}

  /**
   * Animation state for the score smiley. Resolved lazily and cached — the
   * one-time latch is consumed by whichever Flexi emoji renders first, and
   * re-reads on later change-detection passes must return the same value.
   * See FlexiEmojiEntryService.
   */
  private cachedEmojiEntryState: string | null = null;

  get emojiEntryState(): string {
    if (this.cachedEmojiEntryState === null) {
      this.cachedEmojiEntryState = this.emojiEntry.consume()
        ? EMOJI_ENTRY_ANIMATE
        : EMOJI_ENTRY_IDLE;
    }
    return this.cachedEmojiEntryState;
  }

  get score() { return this.viewed.score; }

  /**
   * Customer-facing PDF download. Hits the BE blob endpoint so the file is
   * identical to what the HSE advisor downloaded and what lives in
   * Flexi → Documents → OHS-plans.
   */
  downloadReportPdf(): void {
    if (this.downloading) return;
    const businessId = this.aggregate?.businessId ?? BaseServices.BusinessId;
    if (!businessId) return;
    this.downloading = true;
    this.errorMessage = null;
    // Scope to the review being rendered so an archived one opened from the
    // grid downloads its own report rather than the business's latest.
    this.service.downloadReportPdf(businessId, this.aggregate?.id || undefined).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Named after the review being viewed, so peeking at a past one from
        // the history strip downloads a file dated to THAT review.
        a.download = complianceReportFileName(
          this.translate.instant('FLEXI_COMPLIANCE.REPORT_FILE_NAME'),
          BaseServices.BusinessName,
          this.viewed?.completedOn,
        );
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        this.downloading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.downloading = false;
        this.errorMessage = err.error?.detail ?? this.translate.instant('FLEXI_COMPLIANCE.ERRORS.PDF_FAILED');
      }
    });
  }

  /** Skip removed items in the customer view — they're not part of the score either. */
  visibleItems(section: FlexiComplianceSection): FlexiComplianceLineItem[] {
    return section.items.filter(i => !i.isRemoved);
  }

  /** Active (not removed) items are the only checkpoints that drive section state. */
  private activeItems(section: FlexiComplianceSection): FlexiComplianceLineItem[] {
    return section.items.filter(i => !i.isRemoved);
  }

  sectionRatedCount(section: FlexiComplianceSection): number {
    return this.activeItems(section).filter(i => i.rating !== null && i.rating !== undefined).length;
  }

  sectionTotalCount(section: FlexiComplianceSection): number {
    return this.activeItems(section).length;
  }

  sectionProgressPercent(section: FlexiComplianceSection): number {
    const total = this.sectionTotalCount(section);
    if (total === 0) return 100;
    return Math.round((this.sectionRatedCount(section) / total) * 100);
  }

  /**
   * Section colour follows the highest-priority unresolved checkpoint:
   * red beats yellow, yellow beats green, and green requires every active
   * checkpoint to be completed as green. Resolved red/yellow checkpoints are
   * represented by their current green rating, so the section recalculates
   * from the remaining checkpoint ratings.
   */
  sectionTone(section: FlexiComplianceSection): ComplianceSectionTone {
    const items = this.activeItems(section);
    if (items.length === 0) return 'green';

    const rated = items.filter(i => i.rating !== null && i.rating !== undefined);
    if (rated.length === 0) return 'pending';

    if (rated.some(i => i.rating === this.Rating.Missing)) return 'red';
    if (rated.some(i => i.rating === this.Rating.NeedsImprovement)) return 'yellow';

    return rated.length >= items.length ? 'green' : 'in-progress';
  }

  /**
   * Accordion state: which section is expanded, or null for all collapsed.
   *
   * A completed review has every section rated, so rendering them all open
   * meant a customer opening the report faced ten sections and fifty-odd
   * questions at once with no way to fold any of it away. Collapsed-by-default
   * makes the score and the section tones the landing view, and the detail
   * something you ask for.
   */
  activeSectionId: number | null = null;

  toggleSection(sectionId: number): void {
    this.activeSectionId = this.activeSectionId === sectionId ? null : sectionId;
  }

  isOpen(sectionId: number): boolean {
    return this.activeSectionId === sectionId;
  }

  ratingLabelKey(rating: FlexiComplianceRating | null): string | null {
    if (rating === FlexiComplianceRating.Good) return 'FLEXI_COMPLIANCE.RATINGS.GOOD';
    if (rating === FlexiComplianceRating.NeedsImprovement) return 'FLEXI_COMPLIANCE.RATINGS.NEEDS_IMPROVEMENT';
    if (rating === FlexiComplianceRating.Missing) return 'FLEXI_COMPLIANCE.RATINGS.MISSING';
    return null;
  }

  ratingClass(rating: FlexiComplianceRating | null): string {
    if (rating === FlexiComplianceRating.Good) return 'is-green';
    if (rating === FlexiComplianceRating.NeedsImprovement) return 'is-yellow';
    if (rating === FlexiComplianceRating.Missing) return 'is-red';
    return 'is-unrated';
  }

  smileyClass(): string { return `is-${this.score.smiley}`; }

  /** Bundled image used by the score band (mirrors the advisor-card chip). */
  smileyImage(): string {
    switch (this.score.smiley) {
      case 'green':  return 'assets/images/happy-smile.png';
      case 'yellow': return 'assets/images/neutral-smile.png';
      case 'red':    return 'assets/images/sad-smile.png';
      default:       return 'assets/images/sad-smile.png';
    }
  }
}
